'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import './construction-layers.css';

type Bitmap = { file: string; width: number; height: number };
type LayerAsset = {
  box: { x: number; y: number; width: number; height: number };
  desktop: Bitmap;
  mobile: Bitmap;
};
export type ConstructionManifest = {
  canvas: { width: number; height: number };
  mobileBreakpoint: number;
  base: LayerAsset;
  ambientShadow: LayerAsset;
  layers: Array<LayerAsset & {
    id: string; name: string; z: number;
    motion: { dx: number; dy: number };
  }>;
};
export type ConstructionLayersProps = {
  manifest: ConstructionManifest;
  assetBaseUrl: string;
  includeBase?: boolean;
  ambientShadow?: boolean;
  animated?: boolean;
  paused?: boolean;
  duration?: number;
  intensity?: number;
  quality?: 'auto' | 'desktop' | 'mobile';
  priority?: boolean;
  className?: string;
};

/** Cropped bitmaps; placement and movement remain in the original 1798×875 coordinate system. */
export function ConstructionLayers({
  manifest, assetBaseUrl, includeBase = true, ambientShadow = false,
  animated = true, paused = false, duration = 12, intensity = 1,
  quality = 'auto', priority = false, className = '',
}: ConstructionLayersProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  useEffect(() => {
    const onVisibility = () => setPageVisible(document.visibilityState === 'visible');
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver === 'undefined') setInView(true);
    else {
      observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
      if (ref.current) observer.observe(ref.current);
    }
    return () => { document.removeEventListener('visibilitychange', onVisibility); observer?.disconnect(); };
  }, []);

  const root = assetBaseUrl.replace(/\/$/, '');
  const url = (file: string) => file.startsWith('data:') ? file : root ? `${root}/${file}` : file;
  const amount = Number.isFinite(intensity) ? Math.max(0, Math.min(1, intensity)) : 1;
  const seconds = Number.isFinite(duration) ? Math.max(6, Math.min(60, duration)) : 12;
  const isPaused = paused || !inView || !pageVisible;
  const style = {
    aspectRatio: `${manifest.canvas.width} / ${manifest.canvas.height}`,
    '--gam-loop-duration': `${seconds}s`,
    '--gam-loop-state': isPaused ? 'paused' : 'running',
  } as CSSProperties;
  const placement = (asset: LayerAsset, z: number, motion = { dx: 0, dy: 0 }) => ({
    left: `${100 * asset.box.x / manifest.canvas.width}%`,
    top: `${100 * asset.box.y / manifest.canvas.height}%`,
    width: `${100 * asset.box.width / manifest.canvas.width}%`,
    height: `${100 * asset.box.height / manifest.canvas.height}%`,
    zIndex: z,
    // Transform percentages refer to the cropped wrapper, not the full canvas.
    '--gam-loop-dx': `${100 * motion.dx * amount / asset.box.width}%`,
    '--gam-loop-dy': `${100 * motion.dy * amount / asset.box.height}%`,
  } as CSSProperties);
  const bitmap = (asset: LayerAsset) => {
    const selected = quality === 'mobile' ? asset.mobile : asset.desktop;
    return <picture>
      {quality === 'auto' && <source media={`(max-width: ${manifest.mobileBreakpoint}px)`} srcSet={url(asset.mobile.file)} type="image/webp" />}
      <img src={url(selected.file)} width={selected.width} height={selected.height}
        alt="" decoding="async" loading={priority ? 'eager' : 'lazy'} draggable={false} />
    </picture>;
  };
  return <div ref={ref} className={`gam-construction ${className}`} style={style}
    data-animated={animated} data-paused={isPaused} aria-hidden="true">
    {includeBase && <div className="gam-construction__asset" style={placement(manifest.base, 0)}>{bitmap(manifest.base)}</div>}
    {ambientShadow && <div className="gam-construction__asset" style={placement(manifest.ambientShadow, 1)}>{bitmap(manifest.ambientShadow)}</div>}
    {manifest.layers.map(layer => <div key={layer.id} data-layer={layer.id}
      className="gam-construction__asset gam-construction__part" style={placement(layer, layer.z + 2, layer.motion)}>
      {bitmap(layer)}
    </div>)}
  </div>;
}
